import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { CloseSmall } from "@tiny/icons";
import {
  Tabs,
  HtmlPreview,
  MobilePreview,
  TruncateTooltip,
} from "../../components";
import EmailPreviewDetails from "../EmailPreviewDetails";
import { useTemplatePreviewDialog } from "./TemplatePreviewDialog.hooks";
import { TemplatePreviewDialogProps } from "./TemplatePreviewDialog.types";

function TemplatePreviewDialog(props: TemplatePreviewDialogProps) {
  const {
    mainActionName,
    onMainActionClick,
    open,
    onClose,
    templateName,
    subjectText,
    previewText,
    senderName,
    senderEmail,
  } = props;

  const { activeTab, setActiveTab, handleExited, html, css, isLoading, error } =
    useTemplatePreviewDialog(props);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{ onExited: handleExited }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <TruncateTooltip arrow>
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflowWrap: "break-word",
              }}
              variant="h3"
            >
              {templateName || "Template Preview"}
            </Typography>
          </TruncateTooltip>
          <IconButton onClick={onClose}>
            <CloseSmall />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          mt: subjectText || previewText || senderName || senderEmail ? 0 : 3,
          overflow: "hidden",
        }}
      >
        <EmailPreviewDetails
          subject={subjectText}
          previewText={previewText}
          senderName={senderName}
          senderEmail={senderEmail}
        />

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tab label="Desktop" />
          <Tab label="Mobile" />
        </Tabs>

        <Box
          mt={6}
          display="flex"
          justifyContent="center"
          sx={{ minHeight: "500px" }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="500px"
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="500px"
            >
              <Typography color="error">{error}</Typography>
            </Box>
          ) : activeTab === 0 ? (
            <HtmlPreview
              html={html}
              css={css}
              width="100%"
              height="500px"
              contentWidth={800}
              scrollable
              interactive
            />
          ) : (
            <MobilePreview
              width="260px"
              height="500px"
              html={html}
              css={css}
              contentWidth={360}
              scrollable
              interactive
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ mt: 2 }}>
        <Button variant="contained" color="primary" onClick={onMainActionClick}>
          {mainActionName}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TemplatePreviewDialog;
